import { timeOptions } from "@/lib/utils/timezones";
import type { CustomField } from "@/react/prop-types";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { CustomFieldType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { Pencil } from "@procore/core-icons";
import {
   Box,
   Button,
   Card,
   Flex,
   FlexList,
   Form,
   H1,
   Page,
   Spinner,
   Tearsheet,
   useI18nContext,
} from "@procore/core-react";
import { useToastAlertContext } from "@procore/toast-alert";
import { isAfter } from "date-fns";
import type { FormikProps } from "formik";
import React, { useState } from "react";
import { ErrorBanner } from "../../error-banner";
import type { AssignmentListData, EditAssignmentFormValues } from "./types";
import { useGetAssignmentCreationSupportDataQuery } from "./queries";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { formatOptions, formatPillOptions } from "@/react/shared/helper";
import { renderCustomFields } from "@/react/shared/custom-field-utils";
import { DateUtils } from "@/lib/utils/date";
import { AssignmentConflictModal } from "@/react/shared/modals/assignment-conflicts-modal";
import { validateAssignments } from "./helpers";
import type { BulkEditAssignmentTearsheetPropTypes } from "../../assignment-list/assignment-list-prop-types";
import { useGetProcoreEnvironment } from "../../common/queries/procore-queries";
import { bulkEditAssignmentAndRequestValidationSchema } from "../validation-schema";

export type UpdateAssignmentsPayload = {
   id: string;
   [key: string]: unknown;
};

export const BulkEditAssignmentTearsheet = (props: BulkEditAssignmentTearsheetPropTypes) => {
   const { tableApi, customFields, selectedRows, selectionInProgress, setManuallySelectedRowData } =
      props;
   const { groupId } = useGroupContext();
   const I18n = useI18nContext();
   const [isTearSheetOpen, setIsTearSheetOpen] = useState(false);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(false);
   const { showToast } = useToastAlertContext();
   const validationSchema = bulkEditAssignmentAndRequestValidationSchema(I18n, customFields);
   const { data: assignmentCreationSupportData, isLoading: supportDataLoading } =
      useGetAssignmentCreationSupportDataQuery(groupId);
   const { data: procoreEnvironment } = useGetProcoreEnvironment();
   const [invalidSubset, setInvalidSubset] = useState<
      Array<{ assignment: AssignmentListData; reason: string }>
   >([]);
   const [validSubset, setValidSubset] = useState<AssignmentListData[]>([]);
   const [storedValues, setStoredValues] = useState<EditAssignmentFormValues | null>(null);
   const [storedStartDay, setStoredStartDay] = useState<number | null>(null);
   const [storedEndDay, setStoredEndDay] = useState<number | null>(null);
   const [isFormByAllocation, setIsFormByAllocation] = useState(false);
   const [isFormByTime, setIsFormByTime] = useState(false);

   const initialValues: EditAssignmentFormValues = {
      project: null,
      status: null,
      start_date: null,
      end_date: null,
      start_time: null,
      end_time: null,
      percent_allocated: null,
      resource: null,
   };

   // function for building the payload
   const buildBasePayload = ({
      values,
      formStartDay,
      formEndDay,
      isFormByAllocation,
      isFormByTime,
      validAssignments,
   }: {
      values: EditAssignmentFormValues;
      formStartDay: number | null;
      formEndDay: number | null;
      isFormByAllocation: boolean;
      isFormByTime: boolean;
      validAssignments: AssignmentListData[];
   }): UpdateAssignmentsPayload[] => {
      const payload: any = {
         project_id: values.project?.id,
         resource_id: values.resource?.id,
         status_id: values.status?.id,
         start_day: formStartDay,
         end_day: formEndDay,
         start_time:
            isFormByTime && values.start_time !== undefined && values.start_time !== null
               ? values.start_time.id
               : null,
         end_time:
            isFormByTime && values.end_time !== undefined && values.end_time !== null
               ? values.end_time.id
               : null,
         percent_allocated: isFormByAllocation ? values.percent_allocated : null,
      };

      // Clean empty values
      Object.entries(payload).forEach(([key, value]) => {
         if (
            value === null ||
            value === "" ||
            value === undefined ||
            (typeof value === "object" && Object.keys(value).length === 0)
         ) {
            delete payload[key];
         }
      });

      // if custom fields are present, process them
      if (customFields) {
         const processed = processCustomFields(customFields, values);
         if (processed && Object.keys(processed).length > 0) {
            payload.custom_fields = processed;
         }
      }

      // Check if there's anything to send, to prevent unnecessary API calls
      if (Object.keys(payload).length === 0) {
         return [];
      }

      return validAssignments.map((assignment) => ({
         id: assignment.id,
         ...payload,
      }));
   };

   const postValidAssignments = async (payload: UpdateAssignmentsPayload[]) => {
      try {
         setLoading(true);
         await Assignment2Store.updateAssignmentsStream(payload).stream;
         showToast.success(
            I18n.t("views.company.workforce_planning.assignment.bulk.edit.success", {
               count: payload.length,
            }),
         );
         tableApi?.deselectAll();
         tableApi?.refreshServerSide({ purge: true });
         setIsTearSheetOpen(false);
         setIsModalOpen(false);
         setManuallySelectedRowData([]);
      } catch (err) {
         setError(true);
      } finally {
         setLoading(false);
      }
   };

   const handleSubmit = async (values: EditAssignmentFormValues) => {
      const isFormByAllocation =
         values.percent_allocated !== null && values.percent_allocated !== undefined;
      const isFormByTime =
         (values.start_time !== undefined && values.start_time !== null) ||
         (values.end_time !== undefined && values.end_time !== null);
      const formStartDay = values.start_date ? DateUtils.getDetachedDay(values.start_date) : null;
      const formEndDay = values.end_date ? DateUtils.getDetachedDay(values.end_date) : null;

      // Validate assignments
      const { validAssignments, invalidAssignments } = validateAssignments(
         selectedRows,
         formStartDay,
         formEndDay,
         isFormByAllocation,
         isFormByTime,
         I18n,
      );

      /* istanbul ignore next */
      if (invalidAssignments.length > 0) {
         setInvalidSubset(invalidAssignments);
         setValidSubset(validAssignments);
         setStoredValues(values);
         setStoredStartDay(formStartDay);
         setStoredEndDay(formEndDay);
         setIsFormByAllocation(isFormByAllocation);
         setIsFormByTime(isFormByTime);
         setIsModalOpen(true);
         return;
      }

      // Build payload
      const payload = buildBasePayload({
         values,
         formStartDay,
         formEndDay,
         isFormByAllocation,
         isFormByTime,
         validAssignments,
      });
      // Post valid assignments and prevent unnecessary API calls
      if (payload.length > 0) {
         await postValidAssignments(payload);
      }
   };

   /* istanbul ignore next */
   const handleModalSave = async () => {
      // Build payload
      const payload = buildBasePayload({
         values: storedValues!,
         formStartDay: storedStartDay,
         formEndDay: storedEndDay,
         isFormByAllocation,
         isFormByTime,
         validAssignments: validSubset,
      });
      // Post valid assignments and prevent unnecessary API calls
      if (payload.length > 0) {
         await postValidAssignments(payload);
      }
   };

   const handleClose = () => {
      setIsTearSheetOpen(false);
      setError(false);
      setLoading(false);
   };

   // Function to process custom fields
   function processCustomFields(
      customFields: CustomField[],
      values: Record<string, any>,
   ): { [key: string]: any } {
      return customFields.reduce((acc, customField) => {
         const updatedValue =
            customField.name in values ? values[customField.name] : customField.value;

         if (!customField.integration_only) {
            let processedValue;
            switch (customField.type) {
               case CustomFieldType.DATE:
                  processedValue = updatedValue
                     ? getDetachedDay(new Date(updatedValue))
                     : undefined;
                  break;
               case CustomFieldType.MULTI_SELECT:
                  processedValue =
                     updatedValue && updatedValue.length > 0
                        ? updatedValue.map((v: any) => v.label)
                        : undefined;
                  break;
               case CustomFieldType.SELECT:
                  processedValue = updatedValue ? updatedValue.label : undefined;
                  break;
               default:
                  processedValue = updatedValue;
                  break;
            }
            if (processedValue !== undefined) {
               acc[customField.id as string] = processedValue;
            }
         }
         return acc;
      }, {} as { [key: string]: any });
   }

   if (supportDataLoading) {
      return null;
   }

   return (
      <>
         <Button
            onClick={() => setIsTearSheetOpen(true)}
            icon={<Pencil />}
            variant="tertiary"
            data-testid="edit-btn"
            disabled={selectionInProgress}
         />
         <Tearsheet open={isTearSheetOpen} onClose={handleClose} data-testid="tearsheet">
            <Spinner loading={loading}>
               <Page>
                  <Page.Main
                     style={{
                        width: 800,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                     }}
                  >
                     <Page.Header>
                        {error && (
                           <Page.Banner style={{ width: "100%" }}>
                              <ErrorBanner
                                 title={I18n.t("views.company.workforce_planning.error")}
                                 content={I18n.t(
                                    "views.company.workforce_planning.assignment.bulk.edit.error",
                                 )}
                              />
                           </Page.Banner>
                        )}
                        <Page.Title>
                           <H1>
                              {I18n.t(
                                 "views.company.workforce_planning.assignment.bulk.edit.header",
                              )}
                           </H1>
                        </Page.Title>
                     </Page.Header>

                     <Form
                        view="update"
                        onSubmit={handleSubmit}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                     >
                        {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
                        {(formikProps: FormikProps<EditAssignmentFormValues>) => {
                           return (
                              <Form.Form>
                                 <Page.Body
                                    style={{ width: "100%", height: "100%", marginBottom: 20 }}
                                 >
                                    <Card>
                                       <Box padding="md">
                                          <Form.Row>
                                             <Form.DateSelect
                                                name="start_date"
                                                data-testid="start-date"
                                                colStart={1}
                                                colWidth={6}
                                                onChange={(date) => {
                                                   // Manually set end date when the user selects a start date if end date is not set.
                                                   formikProps.setFieldValue("start_date", date);
                                                   if (!formikProps.values.end_date && date) {
                                                      formikProps.setFieldValue("end_date", date);
                                                   }
                                                }}
                                                label={I18n.t(
                                                   "views.company.workforce_planning.assignment.start_date",
                                                )}
                                             />
                                             <Form.DateSelect
                                                name="end_date"
                                                colStart={7}
                                                colWidth={6}
                                                disabledDate={(date) => {
                                                   const { start_date } = formikProps.values;
                                                   return isAfter(start_date!, date);
                                                }}
                                                label={I18n.t(
                                                   "views.company.workforce_planning.assignment.end_date",
                                                )}
                                             />
                                          </Form.Row>
                                          <Form.Row>
                                             <Form.Select
                                                name="start_time"
                                                options={timeOptions}
                                                colStart={1}
                                                colWidth={6}
                                                onSearch={false}
                                                label={I18n.t(
                                                   "views.company.workforce_planning.assignment.start_time",
                                                )}
                                             />
                                             <Form.Select
                                                name="end_time"
                                                options={timeOptions}
                                                colStart={7}
                                                colWidth={6}
                                                onSearch={false}
                                                label={I18n.t(
                                                   "views.company.workforce_planning.assignment.end_time",
                                                )}
                                             />
                                          </Form.Row>

                                          <Form.Row>
                                             <Form.Select
                                                colStart={1}
                                                colWidth={6}
                                                name="project"
                                                label={I18n.t(
                                                   "views.company.workforce_planning.project",
                                                )}
                                                options={formatOptions(
                                                   assignmentCreationSupportData?.data
                                                      .project_options ?? [],
                                                )}
                                                placeholder={I18n.t(
                                                   "views.company.workforce_planning.select_option",
                                                )}
                                             />

                                             <Form.Select
                                                colStart={7}
                                                colWidth={6}
                                                name="resource"
                                                label={I18n.t(
                                                   "views.company.workforce_planning.labor",
                                                )}
                                                options={formatOptions(
                                                   assignmentCreationSupportData?.data
                                                      .resource_options ?? [],
                                                )}
                                                placeholder={I18n.t(
                                                   "views.company.workforce_planning.assignment.labor_placeholder",
                                                )}
                                             />
                                          </Form.Row>

                                          <Form.Row>
                                             <Form.PillSelect
                                                colStart={1}
                                                colWidth={6}
                                                name="status"
                                                label={I18n.t(
                                                   "views.company.workforce_planning.projects.status",
                                                )}
                                                placeholder={I18n.t(
                                                   "views.company.workforce_planning.select_option",
                                                )}
                                                options={formatPillOptions(
                                                   assignmentCreationSupportData?.data
                                                      .status_options ?? [],
                                                )}
                                             />
                                             <Form.Number
                                                colStart={7}
                                                colWidth={6}
                                                name="percent_allocated"
                                                label={I18n.t(
                                                   "views.company.workforce_planning.assignment.percent_allocated",
                                                )}
                                                placeholder={I18n.t(
                                                   "views.company.workforce_planning.enter_value",
                                                )}
                                             />
                                          </Form.Row>

                                          {customFields && (
                                             <>
                                                <hr />
                                                {renderCustomFields(
                                                   customFields,
                                                   I18n,
                                                   false,
                                                   procoreEnvironment?.currency_symbol ?? "$",
                                                )}
                                             </>
                                          )}
                                       </Box>
                                    </Card>
                                 </Page.Body>
                                 <Page.Footer>
                                    <Box padding="md">
                                       <Flex justifyContent="flex-end" alignItems="center">
                                          <FlexList space="md">
                                             <Button variant="tertiary" onClick={handleClose}>
                                                {I18n.t("views.company.workforce_planning.cancel")}
                                             </Button>
                                             <Button variant="primary" type="submit">
                                                {I18n.t("views.company.workforce_planning.save")}
                                             </Button>
                                          </FlexList>
                                       </Flex>
                                    </Box>
                                 </Page.Footer>
                              </Form.Form>
                           );
                        }}
                     </Form>
                  </Page.Main>
               </Page>
            </Spinner>
         </Tearsheet>

         <AssignmentConflictModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleModalSave}
            validAssignments={validSubset}
            invalidAssignments={invalidSubset}
            selectedCount={selectedRows.length}
         />
      </>
   );
};
